import type { ContactDraft, ContactRecord, ProductInterest } from "./types";

export function emptyProductInterest(): ProductInterest {
  return {
    vault: false,
    operate: false,
    shop: false,
    forge: false,
  };
}

export function parseProductInterest(raw?: Record<string, unknown> | null): ProductInterest {
  const productInterest = (raw && typeof raw === "object" && !Array.isArray(raw) ? raw.product_interest : null) as Record<string, unknown> | null;
  const candidate = productInterest && typeof productInterest === "object" && !Array.isArray(productInterest) ? productInterest : {};
  const pick = (key: keyof ProductInterest) => {
    const value = candidate[key] ?? raw?.[key] ?? raw?.[`product_interest_${key}`] ?? false;
    return Boolean(value === true || value === "true");
  };

  return {
    vault: pick("vault"),
    operate: pick("operate"),
    shop: pick("shop"),
    forge: pick("forge"),
  };
}

export function emptyDraft(accountId: string): ContactDraft {
  return {
    account_id: accountId,
    email: "",
    first_name: "",
    last_name: "",
    title: "",
    phone: "",
    consent_basis: "",
    source_url: "",
    do_not_contact: false,
    product_interest: emptyProductInterest(),
    address_line1: "",
    address_line2: "",
    address_line3: "",
    city: "",
    state_region: "",
    postal_code: "",
    country_code: "",
    country_raw: "",
  };
}

export function draftFromContact(contact: ContactRecord, accountId: string): ContactDraft {
  const raw = contact.raw_attributes && typeof contact.raw_attributes === "object" && !Array.isArray(contact.raw_attributes)
    ? contact.raw_attributes as Record<string, unknown>
    : null;

  return {
    account_id: accountId,
    email: contact.email ?? "",
    first_name: contact.first_name ?? "",
    last_name: contact.last_name ?? "",
    title: contact.title ?? "",
    phone: contact.phone ?? "",
    consent_basis: contact.consent_basis ?? "",
    source_url: String(contact.source_url ?? contact.profile_url ?? raw?.source_url ?? raw?.profile_url ?? "").trim(),
    do_not_contact: contact.do_not_contact === true,
    product_interest: parseProductInterest(contact.raw_attributes ?? null),
    address_line1: contact.address_line1 ?? "",
    address_line2: contact.address_line2 ?? "",
    address_line3: contact.address_line3 ?? "",
    city: contact.city ?? "",
    state_region: contact.state_region ?? "",
    postal_code: contact.postal_code ?? "",
    country_code: contact.country_code ?? "",
    country_raw: contact.country_raw ?? "",
  };
}

export function formatAddressBlock(draft: Pick<
  ContactDraft,
  | "address_line1"
  | "address_line2"
  | "address_line3"
  | "city"
  | "state_region"
  | "postal_code"
  | "country_code"
  | "country_raw"
>): string {
  const lines = [
    draft.address_line1,
    draft.address_line2,
    draft.address_line3,
    [draft.city, draft.state_region, draft.postal_code].filter(Boolean).join(", "),
    draft.country_raw || draft.country_code,
  ].filter((line) => line && String(line).trim());
  return lines.join("\n");
}
